import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {generateTestIds} from '@homebase/common';
import './BaseBox.$BRAND.css.js';

// Inlining the `NEW_BREAKPOINTS` constant here to make it easier to decouple this module
export const breakpoints = {
  all: 'all',
  bp320: 'bp320',
  bp480: 'bp480',
  bp640: 'bp640',
  bp800: 'bp800',
  bp960: 'bp960',
  bp1120: 'bp1120',
  bp1280: 'bp1280',
  bp1440: 'bp1440',
  bp1600: 'bp1600',
};

// Generate classnames for responsive props
// If the propValue is a string or a number, then we apply the styles without a breakpoint
// which means its not within a media query
// Classnames follow the format of `pl-Box--[name]-[value]-[breakpoint]`
// e.g. pl-Box-display-block-640 which means that at screens wider than 640px, the display will be block
// Its basically utility classes tbh, so it doesn't follow our SUIT naming conventions
export function convertResponsivePropToClassnames({
  classNameModifier,
  propValue,
}) {
  const classNamePrefix = 'pl-Box';
  const spacingProps = [
    'm',
    'p',
    'mb',
    'pb',
    'mt',
    'pt',
    'mr',
    'pr',
    'ml',
    'pl',
    'mx',
    'my',
    'px',
    'py',
  ];
  // if the value is a string and for a spacing prop, we need to use inline styles
  if (
    propValue === null ||
    (typeof propValue === 'string' && spacingProps.includes(classNameModifier))
  ) {
    return '';
  }

  // If the value of the prop is a string or a number
  // then we apply that value at all breakpoints,
  if (typeof propValue === 'string' || typeof propValue === 'number') {
    return `${classNamePrefix}--${classNameModifier}-${propValue}`;
  }
  return cx(
    Object.entries(propValue).reduce((classnames, [breakpoint, value]) => {
      let className;
      // If the breakpoint is the `all` value, then we remove the -all modifier
      // to match the naming scheme above and indicate the styles apply at all breakpoints
      if (breakpoint === breakpoints.all) {
        className = `${classNamePrefix}--${classNameModifier}-${value}`;
      } else {
        className = `${classNamePrefix}--${classNameModifier}-${value}-${breakpoint}`;
      }
      return [...classnames, className];
    }, [])
  );
}

// Helper function for getInlineSpacingStyles
// Enforces priority for single-direction (ml, pb, etc.) string values over two-direction (mx, py, etc.)
export function getDirectionalStyle(direction, biDirection) {
  if (typeof direction === 'string') {
    return direction;
  } else if (typeof biDirection === 'string') {
    return biDirection;
  }
  return null;
}

// Generate inline styles for spacing props that are string values and cannot be represented by classnames,
// e.g. "auto", "20%", "5vw", etc.
// `spacingProps` is an object consisting of the subset of component props defining margin or padding.
// returns an object that can be spread into the `style` prop. i.e. {margin: 'auto', paddingLeft: '5%'}
export function getInlineSpacingStyles(spacingProps) {
  const {m, p, mb, pb, mt, pt, mr, pr, ml, pl, mx, my, px, py} = spacingProps;

  return Object.keys(spacingProps).reduce((styles, prop) => {
    // We're only concerned with string values since numeric and object (responsive) values get generated classnames
    if (typeof spacingProps[prop] === 'string') {
      switch (prop) {
        case 'm':
          styles.margin = m;
          break;
        case 'p':
          styles.padding = p;
          break;
        case 'my':
          styles.marginBottom = getDirectionalStyle(mb, my);
          styles.marginTop = getDirectionalStyle(mt, my);
          break;
        case 'mb':
          styles.marginBottom = mb;
          break;
        case 'mt':
          styles.marginTop = mt;
          break;
        case 'py':
          styles.paddingBottom = getDirectionalStyle(pb, py);
          styles.paddingTop = getDirectionalStyle(pt, py);
          break;
        case 'pb':
          styles.paddingBottom = pb;
          break;
        case 'pt':
          styles.paddingTop = pt;
          break;
        case 'mx':
          styles.marginRight = getDirectionalStyle(mr, mx);
          styles.marginLeft = getDirectionalStyle(ml, mx);
          break;
        case 'mr':
          styles.marginRight = mr;
          break;
        case 'ml':
          styles.marginLeft = ml;
          break;
        case 'px':
          styles.paddingRight = getDirectionalStyle(pr, px);
          styles.paddingLeft = getDirectionalStyle(pl, px);
          break;
        case 'pr':
          styles.paddingRight = pr;
          break;
        case 'pl':
          styles.paddingLeft = pl;
          break;
      }
    }
    return styles;
  }, {});
}

function Box({
  is: Element,
  display,
  flexGrow,
  flexShrink,
  flexDirection,
  justifyContent,
  alignItems,
  flexWrap,
  flexOrder,
  textAlign,
  testIds,
  m,
  p,
  mb,
  pb,
  mt,
  pt,
  mr,
  pr,
  ml,
  pl,
  mx,
  my,
  px,
  py,
  position,
  top,
  left,
  right,
  bottom,
  className,
  size,
  'data-hb-id': dataHbId,
  bgColor,
  truncatedLines,
  borderTopColor,
  borderBottomColor,
  borderRightColor,
  borderLeftColor,
  borderColor,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  borderTopLeftRadius,
  borderTopRightRadius,
  borderBottomLeftRadius,
  borderBottomRightRadius,
  borderRadius,
  overflowX,
  overflowY,
  overflow,
  ...rest
}) {
  const responsiveClassNames = [
    // classNameModifier will become the modifier of the classname
    // e.g. 'm' => 'pl-Box--m-5'
    {classNameModifier: 'display', propValue: display},
    // Margin and Padding
    {classNameModifier: 'm', propValue: m},
    {classNameModifier: 'p', propValue: p},
    {classNameModifier: 'mb', propValue: mb},
    {classNameModifier: 'pb', propValue: pb},
    {classNameModifier: 'mt', propValue: mt},
    {classNameModifier: 'pt', propValue: pt},
    {classNameModifier: 'mr', propValue: mr},
    {classNameModifier: 'pr', propValue: pr},
    {classNameModifier: 'ml', propValue: ml},
    {classNameModifier: 'pl', propValue: pl},
    // Horizontal and Vertical margins
    {classNameModifier: 'mx', propValue: mx},
    {classNameModifier: 'my', propValue: my},
    // Horizontal and Vertical padding
    {classNameModifier: 'px', propValue: px},
    {classNameModifier: 'py', propValue: py},
    // Flex props
    {classNameModifier: 'fd', propValue: flexDirection},
    {classNameModifier: 'jc', propValue: justifyContent},
    {classNameModifier: 'ai', propValue: alignItems},
    {classNameModifier: 'fw', propValue: flexWrap},
    {classNameModifier: 'fo', propValue: flexOrder},
    // Text Alignment
    {classNameModifier: 'ta', propValue: textAlign},
    // Positioning
    {classNameModifier: 'position', propValue: position},
    // Flex Basis
    {classNameModifier: 'size', propValue: size},
    {classNameModifier: 'bg', propValue: bgColor},
    {classNameModifier: 'btc', propValue: borderTopColor},
    {classNameModifier: 'bbc', propValue: borderBottomColor},
    {classNameModifier: 'brc', propValue: borderRightColor},
    {classNameModifier: 'blc', propValue: borderLeftColor},
    {classNameModifier: 'bc', propValue: borderColor},
    {classNameModifier: 'btlr', propValue: borderTopLeftRadius},
    {classNameModifier: 'btrr', propValue: borderTopRightRadius},
    {classNameModifier: 'bblr', propValue: borderBottomLeftRadius},
    {classNameModifier: 'bbrr', propValue: borderBottomRightRadius},
    {classNameModifier: 'br', propValue: borderRadius},
    {classNameModifier: 'ox', propValue: overflowX},
    {classNameModifier: 'oy', propValue: overflowY},
    {classNameModifier: 'o', propValue: overflow},
  ].map(convertResponsivePropToClassnames);

  const inlineSpacingStyles = React.useMemo(
    () =>
      getInlineSpacingStyles({
        m,
        p,
        mb,
        pb,
        mt,
        pt,
        mr,
        pr,
        ml,
        pl,
        mx,
        my,
        px,
        py,
      }),
    [m, p, mb, pb, mt, pt, mr, pr, ml, pl, mx, my, px, py]
  );

  return (
    <Element
      {...rest}
      {...generateTestIds({testIds})}
      className={cx(responsiveClassNames, className, {
        [`pl-Box--lines-${truncatedLines}`]: !!truncatedLines,
      })}
      // eslint-disable-next-line react/forbid-component-props
      style={{
        flexGrow,
        flexShrink,
        top,
        left,
        right,
        bottom,
        height,
        minHeight,
        maxHeight,
        width,
        minWidth,
        maxWidth,
        ...inlineSpacingStyles,
      }}
      data-hb-id={dataHbId}
    />
  );
}

Box.propTypes = {
  // The HTML element that the Box will render as.
  is: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.element,
  ]),
  // The CSS display property of the Box
  display: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp320: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp480: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp640: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp800: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp960: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp1120: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp1280: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp1440: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
      bp1600: PropTypes.oneOf([
        'none',
        'flex',
        'block',
        'inline',
        'inline-block',
        'inline-flex',
      ]),
    }),
    PropTypes.oneOf([
      'none',
      'flex',
      'block',
      'inline',
      'inline-block',
      'inline-flex',
    ]),
  ]),
  // The factor that the Box grows by when within a flex container
  flexGrow: PropTypes.oneOf([1, 0, 'auto']),
  // The factor that the Box shrinks by when within a flex container
  flexShrink: PropTypes.oneOf([1, 0, 'auto']),
  // Optional Enzyme and Codeception testing identifiers
  testIds: PropTypes.shape({
    enzyme: PropTypes.string,
    codeception: PropTypes.string,
  }),
  // The margin applied (on all sides) to the Box component
  m: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The padding applied (on all sides) to the Box component
  p: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The bottom margin applied to the Box component
  mb: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The bottom padding applied to the Box component
  pb: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The top margin applied to the Box component
  mt: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The top padding applied to the Box component
  pt: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The right margin applied to the Box component
  mr: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The right padding applied to the Box component
  pr: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The left margin applied to the Box component
  ml: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The left padding applied to the Box component
  pl: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The horizontal (aka "x axis") margin applied to the Box component
  // This will apply margin-left and margin-right to the Box
  mx: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The vertical (aka "y axis") margin applied to the Box component
  // This will apply margin-top and margin-bottom to the Box
  my: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The horizontal (aka "x axis") padding applied to the Box component
  // This will apply padding-left and padding-right to the Box
  px: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The vertical (aka "y axis") padding applied to the Box component
  // This will apply padding-top and padding-bottom to the Box
  py: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]),
    PropTypes.string,
  ]),
  // The positioning applied to the box component
  // Note that this component does not provide any fallbacks for position sticky
  position: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp320: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp480: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp640: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp800: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp960: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp1120: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp1280: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp1440: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
      bp1600: PropTypes.oneOf([
        'absolute',
        'relative',
        'fixed',
        'static',
        'sticky',
      ]),
    }),
    PropTypes.oneOf(['absolute', 'relative', 'fixed', 'static', 'sticky']),
  ]),
  // The top offset applied to the Box, this will only take effect if the `position`
  // prop is provided a value other than `static`. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The bottom offset applied to the Box, this will only take effect if the `position`
  // prop is provided a value other than `static`. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  bottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The left offset applied to the Box, this will only take effect if the `position`
  // prop is provided a value other than `static`. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The right offset applied to the Box, this will only take effect if the `position`
  // prop is provided a value other than `static`. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  right: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // A classname applied to the underlying dom element for the Box
  // @ignore-prop-docs
  className: PropTypes.string,
  // The CSS Flex Direction property applied to the Box component
  flexDirection: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['row', 'column', 'row-reverse', 'column-reverse']),
      bp320: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp480: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp640: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp800: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp960: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp1120: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp1280: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp1440: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
      bp1600: PropTypes.oneOf([
        'row',
        'column',
        'row-reverse',
        'column-reverse',
      ]),
    }),
    PropTypes.oneOf(['row', 'column', 'row-reverse', 'column-reverse']),
  ]),
  // The CSS justify content property applied to the Box component
  justifyContent: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp320: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp480: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp640: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp800: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp960: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp1120: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp1280: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp1440: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
      bp1600: PropTypes.oneOf([
        'flex-start',
        'center',
        'flex-end',
        'space-between',
        'space-evenly',
        'space-around',
      ]),
    }),
    PropTypes.oneOf([
      'flex-start',
      'center',
      'flex-end',
      'space-between',
      'space-evenly',
      'space-around',
    ]),
  ]),
  // The CSS Align Items property applied to the Box component
  alignItems: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp320: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp480: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp640: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp800: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp960: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp1120: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp1280: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp1440: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
      bp1600: PropTypes.oneOf([
        'center',
        'start',
        'end',
        'flex-start',
        'flex-end',
        'stretch',
        'baseline',
      ]),
    }),
    PropTypes.oneOf([
      'center',
      'start',
      'end',
      'flex-start',
      'flex-end',
      'stretch',
      'baseline',
    ]),
  ]),
  // The CSS Flex Wrap property applied to the Box component
  // Only applies when it is a flex container
  flexWrap: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['wrap', 'nowrap']),
      bp320: PropTypes.oneOf(['wrap', 'nowrap']),
      bp480: PropTypes.oneOf(['wrap', 'nowrap']),
      bp640: PropTypes.oneOf(['wrap', 'nowrap']),
      bp800: PropTypes.oneOf(['wrap', 'nowrap']),
      bp960: PropTypes.oneOf(['wrap', 'nowrap']),
      bp1120: PropTypes.oneOf(['wrap', 'nowrap']),
      bp1280: PropTypes.oneOf(['wrap', 'nowrap']),
      bp1440: PropTypes.oneOf(['wrap', 'nowrap']),
      bp1600: PropTypes.oneOf(['wrap', 'nowrap']),
    }),
    PropTypes.oneOf(['wrap', 'nowrap']),
  ]),
  // The alignment of the Box element
  // This is a responsive prop that accepts either an object of breakpoint: alignment
  // or just an alignment value for all breakpoints
  textAlign: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['left', 'center', 'right']),
      bp320: PropTypes.oneOf(['left', 'center', 'right']),
      bp480: PropTypes.oneOf(['left', 'center', 'right']),
      bp640: PropTypes.oneOf(['left', 'center', 'right']),
      bp800: PropTypes.oneOf(['left', 'center', 'right']),
      bp960: PropTypes.oneOf(['left', 'center', 'right']),
      bp1120: PropTypes.oneOf(['left', 'center', 'right']),
      bp1280: PropTypes.oneOf(['left', 'center', 'right']),
      bp1440: PropTypes.oneOf(['left', 'center', 'right']),
      bp1600: PropTypes.oneOf(['left', 'center', 'right']),
    }),
    PropTypes.oneOf(['left', 'center', 'right']),
  ]),
  // The flex order of the Box component
  flexOrder: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp320: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp480: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp640: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp800: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp960: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1120: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1280: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1440: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1600: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    }),
    PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  ]),
  // The flex-basis of the Box
  // This is a responsive prop that accepts either an object of breakpoint: size
  // or just a flex-basis value for all breakpoints
  // This value will derive to flex-basis: calc( 100% / 12 * [gutter size])
  size: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp320: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp480: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp640: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp800: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp960: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1120: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1280: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1440: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
      bp1600: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
    }),
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  ]),
  // Homebase overlay labeling
  'data-hb-id': PropTypes.string,
  // Background color
  bgColor: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['white', 'black--10', 'neutral', 'primary', 'b2b']),
      bp320: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp480: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp640: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp800: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp960: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp1120: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp1280: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp1440: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
      bp1600: PropTypes.oneOf([
        'white',
        'black--10',
        'neutral',
        'primary',
        'b2b',
      ]),
    }),
    PropTypes.oneOf(['white', 'black--10', 'neutral', 'primary', 'b2b']),
  ]),
  // This will apply a top border to the Box
  borderTopColor: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['black--30']),
      bp320: PropTypes.oneOf(['black--30']),
      bp480: PropTypes.oneOf(['black--30']),
      bp640: PropTypes.oneOf(['black--30']),
      bp800: PropTypes.oneOf(['black--30']),
      bp960: PropTypes.oneOf(['black--30']),
      bp1120: PropTypes.oneOf(['black--30']),
      bp1280: PropTypes.oneOf(['black--30']),
      bp1440: PropTypes.oneOf(['black--30']),
      bp1600: PropTypes.oneOf(['black--30']),
    }),
    PropTypes.oneOf(['black--30']),
  ]),
  // This will apply a bottom border to the Box
  borderBottomColor: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['black--30']),
      bp320: PropTypes.oneOf(['black--30']),
      bp480: PropTypes.oneOf(['black--30']),
      bp640: PropTypes.oneOf(['black--30']),
      bp800: PropTypes.oneOf(['black--30']),
      bp960: PropTypes.oneOf(['black--30']),
      bp1120: PropTypes.oneOf(['black--30']),
      bp1280: PropTypes.oneOf(['black--30']),
      bp1440: PropTypes.oneOf(['black--30']),
      bp1600: PropTypes.oneOf(['black--30']),
    }),
    PropTypes.oneOf(['black--30']),
  ]),
  // This will apply a right border to the Box
  borderRightColor: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['black--30']),
      bp320: PropTypes.oneOf(['black--30']),
      bp480: PropTypes.oneOf(['black--30']),
      bp640: PropTypes.oneOf(['black--30']),
      bp800: PropTypes.oneOf(['black--30']),
      bp960: PropTypes.oneOf(['black--30']),
      bp1120: PropTypes.oneOf(['black--30']),
      bp1280: PropTypes.oneOf(['black--30']),
      bp1440: PropTypes.oneOf(['black--30']),
      bp1600: PropTypes.oneOf(['black--30']),
    }),
    PropTypes.oneOf(['black--30']),
  ]),
  // This will apply a left border to the Box
  borderLeftColor: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['black--30']),
      bp320: PropTypes.oneOf(['black--30']),
      bp480: PropTypes.oneOf(['black--30']),
      bp640: PropTypes.oneOf(['black--30']),
      bp800: PropTypes.oneOf(['black--30']),
      bp960: PropTypes.oneOf(['black--30']),
      bp1120: PropTypes.oneOf(['black--30']),
      bp1280: PropTypes.oneOf(['black--30']),
      bp1440: PropTypes.oneOf(['black--30']),
      bp1600: PropTypes.oneOf(['black--30']),
    }),
    PropTypes.oneOf(['black--30']),
  ]),
  // This will apply a border to all 4 sides of the Box
  borderColor: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['black--30']),
      bp320: PropTypes.oneOf(['black--30']),
      bp480: PropTypes.oneOf(['black--30']),
      bp640: PropTypes.oneOf(['black--30']),
      bp800: PropTypes.oneOf(['black--30']),
      bp960: PropTypes.oneOf(['black--30']),
      bp1120: PropTypes.oneOf(['black--30']),
      bp1280: PropTypes.oneOf(['black--30']),
      bp1440: PropTypes.oneOf(['black--30']),
      bp1600: PropTypes.oneOf(['black--30']),
    }),
    PropTypes.oneOf(['black--30']),
  ]),
  // The number of lines that the text content will be truncated at
  truncatedLines: PropTypes.oneOf([null, 1, 2]),
  // The height applied to the Box. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The minHeight applied to the Box. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The maxHeight applied to the Box. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The width applied to the Box. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The minWidth applied to the Box. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The maxWidth applied to the Box. It will be provided directly
  // as inline styles on the element. It supports any CSS value.
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // The border radius of the box applied to the top left corner
  borderTopLeftRadius: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['small', 'medium', 'large']),
      bp320: PropTypes.oneOf(['small', 'medium', 'large']),
      bp480: PropTypes.oneOf(['small', 'medium', 'large']),
      bp640: PropTypes.oneOf(['small', 'medium', 'large']),
      bp800: PropTypes.oneOf(['small', 'medium', 'large']),
      bp960: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1120: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1280: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1440: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1600: PropTypes.oneOf(['small', 'medium', 'large']),
    }),
    PropTypes.oneOf(['small', 'medium', 'large']),
  ]),
  // The border radius of the box applied to the top right corner
  borderTopRightRadius: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['small', 'medium', 'large']),
      bp320: PropTypes.oneOf(['small', 'medium', 'large']),
      bp480: PropTypes.oneOf(['small', 'medium', 'large']),
      bp640: PropTypes.oneOf(['small', 'medium', 'large']),
      bp800: PropTypes.oneOf(['small', 'medium', 'large']),
      bp960: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1120: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1280: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1440: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1600: PropTypes.oneOf(['small', 'medium', 'large']),
    }),
    PropTypes.oneOf(['small', 'medium', 'large']),
  ]),
  // The border radius of the box applied to the bottom left corner
  borderBottomLeftRadius: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['small', 'medium', 'large']),
      bp320: PropTypes.oneOf(['small', 'medium', 'large']),
      bp480: PropTypes.oneOf(['small', 'medium', 'large']),
      bp640: PropTypes.oneOf(['small', 'medium', 'large']),
      bp800: PropTypes.oneOf(['small', 'medium', 'large']),
      bp960: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1120: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1280: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1440: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1600: PropTypes.oneOf(['small', 'medium', 'large']),
    }),
    PropTypes.oneOf(['small', 'medium', 'large']),
  ]),
  // The border radius of the box applied to the bottom right corner
  borderBottomRightRadius: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['small', 'medium', 'large']),
      bp320: PropTypes.oneOf(['small', 'medium', 'large']),
      bp480: PropTypes.oneOf(['small', 'medium', 'large']),
      bp640: PropTypes.oneOf(['small', 'medium', 'large']),
      bp800: PropTypes.oneOf(['small', 'medium', 'large']),
      bp960: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1120: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1280: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1440: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1600: PropTypes.oneOf(['small', 'medium', 'large']),
    }),
    PropTypes.oneOf(['small', 'medium', 'large']),
  ]),
  // The border radius of the box applied to all corners
  borderRadius: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['small', 'medium', 'large']),
      bp320: PropTypes.oneOf(['small', 'medium', 'large']),
      bp480: PropTypes.oneOf(['small', 'medium', 'large']),
      bp640: PropTypes.oneOf(['small', 'medium', 'large']),
      bp800: PropTypes.oneOf(['small', 'medium', 'large']),
      bp960: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1120: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1280: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1440: PropTypes.oneOf(['small', 'medium', 'large']),
      bp1600: PropTypes.oneOf(['small', 'medium', 'large']),
    }),
    PropTypes.oneOf(['small', 'medium', 'large']),
  ]),
  // The horizontal (aka "x-axis") overflow applied to the Box
  overflowX: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp320: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp480: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp640: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp800: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp960: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1120: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1280: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1440: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1600: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
    }),
    PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
  ]),
  // The vertical (aka "y-axis") overflow applied to the Box
  overflowY: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp320: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp480: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp640: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp800: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp960: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1120: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1280: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1440: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1600: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
    }),
    PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
  ]),
  // The overflow (in either axis) applied to the Box
  overflow: PropTypes.oneOfType([
    PropTypes.shape({
      all: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp320: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp480: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp640: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp800: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp960: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1120: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1280: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1440: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
      bp1600: PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
    }),
    PropTypes.oneOf(['hidden', 'visible', 'auto', 'scroll']),
  ]),
};

Box.defaultProps = {
  is: 'div',
  display: null,
  flexGrow: null,
  flexShrink: null,
  flexOrder: null,
  testIds: {},
  m: null,
  p: null,
  mb: null,
  pb: null,
  mt: null,
  pt: null,
  mr: null,
  pr: null,
  ml: null,
  pl: null,
  mx: null,
  my: null,
  px: null,
  py: null,
  className: null,
  flexDirection: null,
  justifyContent: null,
  alignItems: null,
  flexWrap: null,
  textAlign: null,
  position: null,
  top: null,
  left: null,
  right: null,
  bottom: null,
  size: null,
  'data-hb-id': null,
  bgColor: null,
  truncatedLines: null,
  borderTopColor: null,
  borderBottomColor: null,
  borderRightColor: null,
  borderLeftColor: null,
  borderColor: null,
  height: null,
  minHeight: null,
  maxHeight: null,
  width: null,
  minWidth: null,
  maxWidth: null,
  borderTopLeftRadius: null,
  borderTopRightRadius: null,
  borderBottomLeftRadius: null,
  borderBottomRightRadius: null,
  borderRadius: null,
  overflowX: null,
  overflowY: null,
  overflow: null,
};

Box.breakpoints = breakpoints;
Box.convertResponsivePropToClassnames = convertResponsivePropToClassnames;

export default Box;
